import React, { useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { sendGAEvent } from '@noths/polaris-client-google-analytics';

import { attachHrefToUIFacets } from 'src/adapters/storeToPageQueryString/attachHrefToUIFacets';
import { adaptFacetToUIFacet } from 'src/adapters/storeToUI/adaptFacetToUIFacet';
import { adaptSortOptionsToUISortOptions } from 'src/adapters/storeToUI/adaptSortOptionsToUISortOptions';
import { ListingsControls } from 'src/components/organisms/ListingsControls/ListingsControls';
import type {
  ChangedFacetDetails,
  ChangedSortDetails,
} from 'src/components/organisms/ListingsControlsOptions/types';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { updatePageURLWithQuery } from 'src/redux/products/thunks/updatePageURLWithQuery';
import { TriggerName } from 'src/redux/products/types';
import { actions } from 'src/redux/productsQuery/slice';
import { scrollToTopOfProductsList } from 'src/utils/scrollToTopOfProductsList';

export const ListingsControlsContainer = () => {
  const dispatch = useDispatch();
  const state = useSelector((s: ReduxApplicationState) => s);

  const facets = useMemo(() => {
    const uiFacet = adaptFacetToUIFacet(state.productsQuery.facets);

    return attachHrefToUIFacets(state, uiFacet);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `state` is used in the callback; facet hrefs only change when query, path, or hash change
  }, [state.productsQuery, state.router.location.pathname, state.navigation.hash]);

  const sortOptions = state.productsQuery.sortOptions;

  const onAllFacetsVisible = () => {
    sendGAEvent({
      event: 'custom_event',
      event_category: 'Filters',
      event_action: 'filter impression',
      event_label: 'all filters viewed',
    });
  };

  const onSortOptionChange = ({ activeValue }: ChangedSortDetails) => {
    batch(() => {
      dispatch(actions.changeSortOption(activeValue));
      dispatch(requestProducts({ triggerName: TriggerName.CHANGE_SORT }));
      dispatch(updatePageURLWithQuery());

      scrollToTopOfProductsList();
    });
  };

  const onFacetOptionChange = ({ ...changedFacet }: ChangedFacetDetails) => {
    const changedOption = changedFacet.options[0];

    batch(() => {
      dispatch(actions.changeFacetOption(changedFacet));
      dispatch(
        requestProducts({
          triggerName: TriggerName.CHANGE_FACETS,
          metadata: {
            facetTitle: changedFacet.title,
            optionTitle: changedOption.title!,
            optionActive: changedOption.active,
          },
        }),
      );
      dispatch(updatePageURLWithQuery());

      scrollToTopOfProductsList();
    });
  };

  return (
    <ListingsControls
      facets={facets}
      onAllFacetsVisible={onAllFacetsVisible}
      onFacetOptionChange={onFacetOptionChange}
      onSortOptionChange={onSortOptionChange}
      sortOptions={adaptSortOptionsToUISortOptions(sortOptions)}
    />
  );
};
